<template>
  <div
    v-if="userStore.language"
    class="pb-home"
  >
    <div class="pb-home-content pb-home-profiles-list">
      <!-- <pre>{{ sortOptions }}</pre> -->
      <DataViewHeader
        class="pb-home-profiles-list-header"
        refresh
        :readonly="loading"
        @refresh="loadProfiles"
      >
        <div class="pb-home-profiles-list-header-content">
          <div class="pb-home-profiles-list-header-content-left">
            <InlineSelectorContainer class="pb-home-profiles-list-filter">
              <InlineSelectorItem
                disabled-class="base-button-disabled"
                :disabled="loading"
                :to="{
                  ...$route,
                  query: {
                    ...$route.query,
                    filter: FILTER_CERTIFIED,
                    page: 1,
                  },
                }"
                :as="PbLink"
                :match-query="['filter']"
              >
                {{ translate('generic.official') }}
              </InlineSelectorItem>
              <InlineSelectorItem
                disabled-class="base-button-disabled"
                :disabled="loading"
                :to="{
                  ...$route,
                  query: {
                    ...$route.query,
                    filter: FILTER_MINE,
                    page: 1,
                  },
                }"
                :as="PbLink"
                :match-query="['filter']"
              >
                {{ translate('generic.mine') }}
              </InlineSelectorItem>
              <InlineSelectorItem
                disabled-class="base-button-disabled"
                :disabled="loading"
                :to="{
                  ...$route,
                  query: {
                    ...$route.query,
                    filter: FILTER_ALL,
                    page: 1,
                  },
                }"
                :as="PbLink"
                :match-query="['filter']"
              >
                {{ translate('generic.all') }}
              </InlineSelectorItem>
              <InlineSelectorItem
                disabled-class="base-button-disabled"
                :disabled="loading"
                :to="{
                  ...$route,
                  query: {
                    ...$route.query,
                    filter: FILTER_TRASH,
                    page: 1,
                  },
                }"
                :as="PbLink"
                :match-query="['filter']"
              >
                {{ translate('generic.trash') }}
              </InlineSelectorItem>
            </InlineSelectorContainer>
            <div class="pb-home-profiles-list-search">
              <PbInput
                v-model="tempName"
                :label="translate('generic.name')"
                class="pb-home-profile-list-name-search"
                dynamic
                :disabled="loading"
                @keypress.enter="applySearch"
              />
              <BrandsField
                v-model="tempBrand"
                allow-all
                :disabled="loading"
                class="pb-home-profile-list-brand-search"
              />
            </div>
          </div>
          <div class="pb-home-profiles-list-header-content-right">
            <ControlButton
              v-if="searchEnabled || !hasSearched"
              icon="search"
              :disabled="!searchEnabled || loading"
              class=" pb-home-profile-list-search-btn btn-success"
              :to="getSearchRoute({ name: tempName, brand: tempBrand })"
            />
            <ControlButton
              v-else
              icon="remove"
              class=" pb-home-profile-list-search-btn btn-warning"
              :disabled="loading"
              :to="getSearchRoute()"
            />
          </div>
        </div>
      </DataViewHeader>

      <UiTable
        v-if="items.length || loading"
        id="profiles-list"
        :is-loading="loading"
        :loading-row-count="loadingCount"
        class="pb-home-profiles-list-table"
      >
        <template #header>
          <DataViewSortHeader
            field="name"
            class="pb-home-profiles-list-column-header pb-home-profiles-list-column-name"
            router
            :by="sortBy"
            :order="sortOrder"
            :readonly="loading"
          >
            {{ translate('generic.name') }}
          </DataViewSortHeader>
          <DataViewSortHeader
            field="brand"
            class="pb-home-profiles-list-column-header pb-home-profiles-list-column-brand"
            router
            :by="sortBy"
            :order="sortOrder"
            :readonly="loading"
          >
            {{ translate('generic.brand') }}
          </DataViewSortHeader>
          <TableCell
            class="pb-home-profiles-list-column-header pb-home-profiles-list-column-owner"
            head
            left
          >
            {{ translate('generic.owner') }}
          </TableCell>
          <DataViewSortHeader
            field="updatedDate"
            class="pb-home-profiles-list-column-header pb-home-profiles-list-column-updated"
            router
            :by="sortBy"
            :order="sortOrder"
            :readonly="loading"
          >
            {{ translate('generic.updatedDate') }}
          </DataViewSortHeader>
          <TableCell
            class="pb-home-profiles-list-column-header pb-home-profiles-list-column-icons"
            head
            center
          >
            <UiIcon
              v-tooltip="{ text: translate('pb2.profilePropertiesView.profileWorksWithWolfmixLabel') }"
              icon="www"
              color="muted"
              class="pb-icon-cell-item"
              size="medium"
            />
          </TableCell>
          <TableCell
            class="pb-home-profiles-list-column-header pb-home-profiles-list-column-icons"
            head
            center
          >
            <UiIcon
              v-tooltip="{ text: translate('generic.official') }"
              icon="certified"
              color="muted"
              class="pb-icon-cell-item"
            />
          </TableCell>
          <TableCell
            class="pb-home-profiles-list-column-header pb-home-profiles-list-column-icons"
            head
            center
          >
            <UiIcon
              v-tooltip="{ text: translate('pb2.downloadProfile.label') }"
              icon="download"
              color="muted"
              class="pb-icon-cell-item"
            />
          </TableCell>
        </template>

        <TableRow v-for="item in items" :key="item.uid">
          <TableCell
            :to="item.link"
            class="pb-home-profiles-list-column-body pb-home-profiles-list-column-name"
            strong
          >
            {{ item.name }}
          </TableCell>
          <TableCell
            :to="item.link"
            class="pb-home-profiles-list-column-body pb-home-profiles-list-column-brand"
            muted
          >
            {{ item.brand }}
          </TableCell>
          <TableCell
            :to="item.link"
            class="pb-home-profiles-list-column-body pb-home-profiles-list-column-owner"
            muted
          >
            <template v-if="filter === FILTER_CERTIFIED">
              {{ translate('generic.official') }}
            </template>
            <template v-else>
              {{ (item.owner && item.owner.displayName) || translate('generic.noOwner') }}
            </template>
          </TableCell>
          <TableCell
            :to="item.link"
            class="pb-home-profiles-list-column-body pb-home-profiles-list-column-updated"
            muted
          >
            {{ item.formattedUpdatedDate }}
          </TableCell>
          <TableCell
            :to="item.link"
            class="pb-home-profiles-list-column-body pb-home-profiles-list-column-icons pb-icon-cell"
            center
          >
            <UiIcon
              v-if="item.worksWithWolfmix"
              v-tooltip="{ text: translate('pb2.profilePropertiesView.profileWorksWithWolfmixLabel') }"
              icon="www"
              size="medium"
              class="pb-icon-cell-item pb-works-with-wolfmix-icon"
            />
          </TableCell>
          <TableCell
            :to="item.link"
            class="pb-home-profiles-list-column-body pb-home-profiles-list-column-icons pb-icon-cell"
            center
          >
            <UiIcon
              v-if="item.isCertificated"
              v-tooltip="{ text: translate('generic.official') }"
              icon="certified"
              color="success"
              class="pb-icon-cell-item"
            />
          </TableCell>
          <TableCell
            v-if="item.isRemove"
            clickable
            class="pb-home-profiles-list-column-body pb-home-profiles-list-column-icons pb-icon-cell"
            center
            @select="askRestoreConfirmation(item)"
          >
            <UiIcon
              v-tooltip="{ text: translate('generic.trash') }"
              icon="trash"
              color="muted"
              class="pb-icon-cell-item"
            />
          </TableCell>
          <TableCell
            v-else-if="item.isValid"
            :to="item.downloadLink"
            class="pb-home-profiles-list-column-body pb-home-profiles-list-column-icons pb-icon-cell"
            center
          >
            <UiIcon
              v-tooltip="{ text: translate('pb2.downloadProfile.label') }"
              class="pb-icon-cell-item"
              icon="download"
            />
          </TableCell>
          <TableCell
            v-else
            :to="item.link"
            class="pb-home-profiles-list-column-body pb-home-profiles-list-column-icons pb-icon-cell"
            center
          >
            <UiIcon
              v-tooltip="{ text: translate('pb2.downloadProfile.labelDisabled') }"
              class="pb-icon-cell-item"
              icon="warning"
              color="error"
            />
          </TableCell>
        </TableRow>

        <!--
          <template #grid="{ item }">
            <div class="card-header">
              <div class="pb-home-card-meta">
                <UiIcon
                  v-if="item.worksWithWolfmix"
                  v-tooltip="{
                    text: translate('pb2.profilePropertiesView.profileWorksWithWolfmixLabel')
                  }"
                  class="pb-works-with-wolfmix-icon"
                  icon="www"
                  size="medium"
                />
                <UiIcon
                  v-if="item.isRemove"
                  v-tooltip="{ text: translate('generic.trash') }"
                  icon="trash"
                  color="muted"
                />
                <template v-else>
                  <UiIcon
                    v-if="!item.isValid"
                    v-tooltip="{ text: translate('pb2.downloadProfile.labelDisabled') }"
                    icon="warning"
                    color="error"
                  />
                  <TableButton
                    v-else
                    v-tooltip="{ text: translate('pb2.downloadProfile.label') }"
                    :to="buildDownloadLink(item.uid, 'ssl2')"
                  >
                    <UiIcon icon="download" />
                  </TableButton>
                </template>
              </div>
              <h1 v-tooltip="{ text: item.name }">
                {{ item.name }}
              </h1>
            </div>
            <div class="card-icon">
              <Asset
                src="Images/Icons/files/v2/ssl@3x.png"
                :placeload-centered="true"
                alt="Profile image"
              />
            </div>
            <div class="card-footer">
              <div>{{ item.brand }}</div>
              <div>{{ item.formattedUpdatedDate }}</div>
            </div>
          </template>
        -->
      </UiTable>
      <NoItemFound v-else />

      <DataViewFooter
        pagination
        goto
        router
        :readonly="loading"
        :max-pages="5"
        :page-count="lastPage"
        :current-page="page"
        @goto="loadPage"
      >
        <SelectField
          class="pb-home-profiles-list-limit"
          :value="limit"
          :aria-label="translate('pb2.homeView.limitLabel')"
          :options="limitOptions"
          :disabled="loading"
          :size="SIZE_SMALL"
          @input="applyLimit"
        />
        <UiTypo>
          {{ from }}-{{ to }} / {{ numItems }}
        </UiTypo>
      </DataViewFooter>
    </div>

    <BaseButton
      tooltip="Show release notes"
      class="pb-home-release-version"
      @click="openReleaseNotes"
    >
      v{{ version }}
    </BaseButton>
  </div>
  <ConfirmDialog
    v-bind="restoreConfirmControl"
    confirm-style="success"
    confirm-text="pb2.homeView.restoreConfirmLabel"
    :dialog-title="translate('pb2.homeView.restoreConfirmTitle')"
  >
    {{ translate('pb2.homeView.restoreConfirmMessage') }} "{{ toRestore?.name }}" ?
  </ConfirmDialog>
</template>

<script setup>
import {
  computed,
  watch,
  ref,
  toRef,
} from 'vue'
import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
// import Asset from '@nicolaudie-group/ng-ui-kit/components/Asset/Asset.vue'
import DataViewHeader from '@nicolaudie-group/ng-ui-kit/components/DataView/DataViewHeader.vue'
import DataViewFooter from '@nicolaudie-group/ng-ui-kit/components/DataView/DataViewFooter.vue'
import DataViewSortHeader from '@nicolaudie-group/ng-ui-kit/components/DataView/DataViewSortHeader.vue'
import NoItemFound from '@nicolaudie-group/ng-ui-kit/components/NoItemFound/NoItemFound.vue'
import TableRow from '@nicolaudie-group/ng-ui-kit/components/Table/TableRow.vue'
import TableCell from '@nicolaudie-group/ng-ui-kit/components/Table/TableCell.vue'
import UiTable from '@nicolaudie-group/ng-ui-kit/components/Table/UiTable.vue'
import UiTypo from '@nicolaudie-group/ng-ui-kit/components/UiTypo/UiTypo.vue'
// import InlineSelector from
// '@nicolaudie-group/ng-ui-kit/components/InlineSelector/InlineSelector.vue'
import InlineSelectorContainer from '@nicolaudie-group/ng-ui-kit/components/InlineSelector/InlineSelectorContainer.vue'
import InlineSelectorItem from '@nicolaudie-group/ng-ui-kit/components/InlineSelector/InlineSelectorItem.vue'
import ControlButton from '@nicolaudie-group/ng-ui-kit/components/ControlButton/ControlButton.vue'
import { SORT_ORDERS } from '@nicolaudie-group/ng-ui-kit/composables/useDataViewSort'
import { isPending } from '@nicolaudie-group/ng-ui-kit/helpers/isStatus'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import useStatus from '@nicolaudie-group/ng-ui-kit/composables/useStatus'
import useCloseOthers from '@nicolaudie-group/ng-ui-kit/composables/useCloseOthers'
import useConfirmDialog from '@nicolaudie-group/ng-ui-kit/composables/useConfirmDialog'
import usePagination from '@nicolaudie-group/ng-ui-kit/composables/usePagination'

import useTranslate from '@/composables/useTranslate'
// import useSmartSettings from '@/composables/useSmartSettings'
// import useViewMode from '@/composables/useViewMode'
import useUserStore from '@/stores/user.store'
import useProfileStore from '@/stores/profile.store'
import formatDate from '@/helpers/formatDate'
import useRouterStore from '@/stores/router.store'
import routeNames from '@/router/names'
// import TableButton from '@/components/TableButton.vue'
import BrandsField from '@/components/BrandsField/BrandsField.vue'
import SelectField from '@/components/SelectField/SelectField.vue'
import PbInput from '@/components/PbInput.vue'
import { SIZE_SMALL } from '@nicolaudie-group/ng-ui-kit/components/Listbox/symbols'
import buildDownloadLink from '@/helpers/profile/buildDownloadLink'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import useQuerySync from '@/composables/useQuerySync'
import PbLink from '@/components/PbLink.vue'
import useQueries from '@/composables/useQueries'
import ALL_FILTERS, {
  FILTER_MINE,
  FILTER_ALL,
  FILTER_CERTIFIED,
  FILTER_TRASH,
} from '@/helpers/homeFilters'
import { onBeforeRouteLeave } from 'vue-router'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import useReleaseNotes from '@/composables/useReleaseNotes'

const userStore = useUserStore()
const profileStore = useProfileStore()
const routerStore = useRouterStore()

// Filters
const DEFAULT_FILTER = FILTER_MINE
const DEFAULT_NAME = ''
const DEFAULT_BRAND = ''

const route = toRef(routerStore, 'route')

function getSearchRoute({ name, brand } = {}) {
  const currentRoute = route.value
  return {
    ...currentRoute,
    query: {
      ...currentRoute.query,
      name: name || undefined,
      brand: brand || undefined,
      page: 1,
    },
  }
}

/**
 * Make sure we always get a number, instead of strings or undefined
 */
function parseNumberQuery(value, fallback = 1) {
  const result = parseInt(String(value), 10)
  return Number.isNaN(result) ? fallback : result
}

/**
 * Make sure we always get a string, instead of undefined
 */
function parseStringQuery(value) {
  return value || ''
}

/**
 * Make sure we always get a valid filter from the enum, instead of strings or undefined
 */
function fixFilterQuery(value) {
  if (ALL_FILTERS.includes(value)) {
    return value
  }
  // Fallback to the default filter if unrecognized
  return DEFAULT_FILTER
}

const SORTBY_NAME = 'name'
const SORTBY_BRAND = 'brand'
const SORTBY_UPDATED = 'updatedDate'
const ALL_SORTBY = [SORTBY_NAME, SORTBY_BRAND, SORTBY_UPDATED]

// Settings
const ALL_LIMITS = [5, 10, 20, 50]

const limitOptions = ALL_LIMITS.map((currentLimit) => ({
  value: currentLimit,
  label: String(currentLimit),
}))

const DEFAULT_LIMIT = 10
const DEFAULT_SORTBY = SORTBY_UPDATED
const DEFAULT_SORTORDER = SORT_ORDERS.DESC

function fixLimitQuery(value) {
  const num = parseNumberQuery(value, DEFAULT_LIMIT)
  return ALL_LIMITS.includes(num) ? num : DEFAULT_LIMIT
}

function getLimitRoute(limit = DEFAULT_LIMIT) {
  const currentRoute = route.value
  return {
    ...currentRoute,
    query: {
      ...currentRoute.query,
      limit: fixLimitQuery(limit),
      page: 1,
    },
  }
}

function fixSortByQuery(value) {
  if (ALL_SORTBY.includes(value)) {
    return value
  }
  return DEFAULT_SORTBY
}

function fixSortOrderQuery(value) {
  if (value === SORT_ORDERS.ASC || value === SORT_ORDERS.DESC) {
    return value
  }
  return DEFAULT_SORTORDER
}

// Get stuff from profile store
const items = computed(() => (
  (profileStore.listData ? profileStore.listData.data : []).map((item) => ({
    ...item,
    formattedUpdatedDate: formatDate(item.updatedDate),
    link: {
      name: routeNames.Profile,
      params: {
        profileUid: item.uid,
      },
    },
    downloadLink: buildDownloadLink(item.uid, 'ssl2'),
  }))
))
const totalItems = computed(() => (
  profileStore.listData ? profileStore.listData.pagination.count : 0
))

// const { translate, translationsLoaded } = useTranslate()
const { translate } = useTranslate()

const { success: listLoaded } = useStatus(toRef(profileStore, 'listStatus'))

const bidirectional = ref(true)

/**
 * Curent filter.
 *
 * This needs to be synced from and to query, so that it gets auto fixed to a valid enum
 * value by fixFilterQuery
 */
const filter = useQuerySync('filter', {
  initial: DEFAULT_FILTER,
  bidirectional,
  parse: fixFilterQuery,
  serialize: fixFilterQuery,
})

/**
 * Ref to temporary hold the name filter input by the user, before it gets committed to
 * the query filters when the user confirms the search.
 *
 * We only use one-way sync from query, because we just want the input to get the value
 * provided in queries, but don't want the query to update until search is confirmed.
 */
const tempName = useQuerySync('name', {
  initial: DEFAULT_NAME,
  bidirectional: false,
  parse: parseStringQuery,
})
const tempBrand = useQuerySync('brand', {
  initial: DEFAULT_BRAND,
  bidirectional: false, // Only sync from query, not to query
  parse: parseStringQuery,
})

const limit = useQuerySync('limit', {
  initial: DEFAULT_LIMIT,
  bidirectional, // Sync from and to query
  parse: fixLimitQuery,
  serialize: fixLimitQuery,
})
const sortBy = useQuerySync('sortBy', {
  initial: DEFAULT_SORTBY,
  bidirectional, // Sync from and to query
  parse: fixSortByQuery,
  serialize: fixSortByQuery,
})
const sortOrder = useQuerySync('sortOrder', {
  initial: DEFAULT_SORTORDER,
  bidirectional, // Sync from and to query
  parse: fixSortOrderQuery,
  serialize: fixSortOrderQuery,
})

const {
  page,
  lastPage,
  itemsInPage,
  from,
  to,
  numItems,
  goto,
} = usePagination(totalItems, {
  limit,
  page: useQuerySync('page', {
    initial: 1,
    bidirectional,
    parse: parseNumberQuery,
  }),
  // Autofix page number only when the list was loaded, so that we know the maximum number of pages
  autofix: listLoaded,
})

async function applyLimit(newLimit) {
  return routerStore.push(getLimitRoute(newLimit))
}

/*
Here, we get the filters directly from the queries.
That way, there is only one source of truth.

The purpose of the various refs and querySyncs above is to sync
the inputs values from the queries, and still hold a temporary
state before committing those values to the queries.
*/
const queries = useQueries()
/*
Using oldValue provided to computedGetter to detect changes (not yet documented):
https://github.com/vuejs/core/pull/9497
*/
const filters = computed((oldValue) => {
  const filterQuery = fixFilterQuery(queries.filter)

  let owner
  switch (filterQuery) {
    case FILTER_MINE:
      owner = 'mine'
      break
    case FILTER_ALL:
      owner = 'all'
      break
    default:
  }

  const previous = oldValue || {}

  const result = {
    ...previous,
    page: parseNumberQuery(queries.page),
    limitBy: parseNumberQuery(queries.limit, DEFAULT_LIMIT),
    sortBy: queries.sortBy,
    sortOrder: queries.sortOrder,
    name: queries.name || undefined,
    brand: queries.brand || undefined,
    isCertificated: filterQuery === FILTER_CERTIFIED || undefined,
    ownerUid: owner,
    isRemove: filterQuery === FILTER_TRASH || undefined,
  }

  /*
  Since this is a computed depending on queries, the compute function will run
  everytime a query changes, even if this is one we don't care about for filters,
  or even if the corresponding filter already has the right value.

  So we check that at least one of the filters (queries we care about) changed,
  in which case we return a new filters object. Otherwise we can just return
  previous unchanged filters object, to break the effect chain.
  */
  // For each property
  const keys = Object.keys(result)
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]
    // If the property was changed, return the new object with modified values
    if (result[key] !== previous[key]) {
      dev.info('Filters changed', previous, result)
      return result
    }
  }

  dev.info('Filters unchanged', previous, result)
  // If no property was changed, return the previous ref so that watchers/computed are not triggered
  return previous
})

const hasSearched = computed(() => {
  const hasName = !!parseStringQuery(queries.name)
  const hasBrand = !!parseStringQuery(queries.brand)
  return hasName || hasBrand
})

const searchEnabled = computed(() => {
  const hasNameInput = !!parseStringQuery(tempName.value)
  const hasBrandInput = !!parseStringQuery(tempBrand.value)

  const nameChanged = parseStringQuery(queries.name) !== tempName.value
  const brandChanged = parseStringQuery(queries.brand) !== tempBrand.value

  /*
  Enable the "search" button if a name or brand was entered, and
  if it's different from the current search
  */
  return (hasNameInput || hasBrandInput) && (nameChanged || brandChanged)
})

function applySearch() {
  return routerStore.push(getSearchRoute({
    name: tempName.value,
    brand: tempBrand.value,
  }))
}

/*
  When navigating away to a route that doesn't use the queries, this will trigger one last
  change the various values synced to queries (they will change to undefined).

  That change will cause the various watchers on those values (ie. page or limit autofix) to change
  it back from undefined to some fallback, and update the queries again in the URL with those
  fallbacks.

  Since we don't want those queries we navigated away from back in the URL, we stop syncing from ref
  to query when there is a route change that will unmount the component (onBeforeRouteLeave)

  See:
  https://router.vuejs.org/guide/advanced/navigation-guards.html#Using-the-options-API
  https://router.vuejs.org/guide/advanced/navigation-guards.html#The-Full-Navigation-Resolution-Flow
  */
onBeforeRouteLeave(() => {
  bidirectional.value = false
})

async function loadProfiles() {
  dev.info('Loading profiles', filters.value)
  await profileStore.getList(filters.value)
}

const loadingCount = computed(() => (itemsInPage.value > 0 ? itemsInPage.value : 10))

const loading = computed(() => isPending(profileStore.listStatus))

function loadPage(newPage) {
  // Disable pagination when loading
  if (!loading.value) {
    goto(newPage)
  }
}

// Load the profiles when the tracked values or the page changes
watch(
  filters,
  loadProfiles,
  {
    // Load the profiles on initialization
    immediate: true,
    /*
    Wait for the component to be ready (rendered, or in this case what we want is unmounted)
    before loading the profiles, so that it's not triggered needlessly when
    navigating away because the queries changes to undefined
    */
    flush: 'post',
  },
)

const toRestore = ref(null)

async function restoreAndOpenProfile() {
  const { uid, link } = toRestore.value
  await profileStore.restore(uid)
  return routerStore.push(link)
}

const {
  control: restoreConfirmControl,
  open: openRestoreConfirm,
  cancel: cancelRestoreConfirm,
  state: restoreConfirmState,
} = useConfirmDialog(restoreAndOpenProfile)

useCloseOthers(cancelRestoreConfirm, restoreConfirmState)

function askRestoreConfirmation(item) {
  toRestore.value = item
  return openRestoreConfirm()
}

const { openDialog: openReleaseNotes, version } = useReleaseNotes()
</script>

<style lang="scss" src="./HomeView.scss" />
